/**
 * versona_bodies — which body a versona wears (PURE; 2026-10-08, versona wave 4).
 *
 * Before wave 4 every civilian of a civilisation wore ONE body (the Aurelian citizen, the Borean citizen): a town of
 * ninety was two faces. Wave 4 adds twenty bodies (`tools/asset-pipeline/versona-wave4.json`): per civilisation five
 * more adults and two elders, a second trader, two town guards, a second instructor, two civ-less colonists.
 *
 * THE RULE (deterministic by id, the same on every client and for every caller — the crowd, the battle's stored looks,
 * the talk window's voice, the Universe preview):
 *   · a CIVILIAN of a known civilisation: an ELDER (the id's own age hash, `founderIsElder` — the same test that gives
 *     them the elderly walk) wears one of that people's elder bodies; everyone else one of its adult bodies;
 *   · any other archetype: its pool (the pre-wave bodies + wave 4's);
 *   · an unknown archetype is a civilian of no civilisation.
 * THE BASE BODIES (the ten that existed before the wave) are what the world loads at entry and what stands in while a
 * wave-4 twin streams: `baseAvatarFor` is the pre-wave rule, bit for bit. Wave-4 twins are loaded ON DEMAND
 * (EnvironmentModule.wantBody) — a town fetches only the bodies its own people wear.
 *
 * Off switch: `localStorage atlas:versona-variety = 'off'` (read once per page) — the pre-wave bodies only.
 */
import { founderIsElder } from './versona_clips.js';

/** The pre-wave pools (loaded at world entry; every fallback lands on one of these). */
export const BASE_ARCHETYPE_AVATARS: Readonly<Record<string, readonly string[]>> = {
  civilian: ['avatar:colonist_jumpsuit_a', 'avatar:colonist_jumpsuit_b', 'avatar:child_colonist'],
  vendor: ['avatar:merchant_robed'],
  trainer: ['avatar:official_uniform'],
  guard: ['avatar:env_suit_worker'],
  entertainer: ['avatar:alien_humanoid_a', 'avatar:alien_humanoid_b'],
};
/** The pre-wave civilian body of each civilisation. */
export const BASE_CIV_CIVILIAN: Readonly<Record<string, string>> = {
  a: 'avatar:civ_aurelian_citizen',
  b: 'avatar:civ_borean_citizen',
};

/** Wave 4, by archetype pool. */
export const EXTRA_ARCHETYPE_AVATARS: Readonly<Record<string, readonly string[]>> = {
  civilian: ['avatar:colonist_drifter_m', 'avatar:colonist_pilgrim_f'],
  vendor: ['avatar:vendor_trader_f'],
  trainer: ['avatar:trainer_instructor_m'],
  guard: ['avatar:guard_militia_f', 'avatar:guard_militia_m'],
};
/** Wave 4: each civilisation's further ADULT civilians. */
export const CIV_ADULTS: Readonly<Record<string, readonly string[]>> = {
  a: ['avatar:civ_aurelian_weaver_f', 'avatar:civ_aurelian_fieldhand_m', 'avatar:civ_aurelian_artisan_f', 'avatar:civ_aurelian_youth_m', 'avatar:civ_aurelian_scholar_f'],
  b: ['avatar:civ_borean_parka_f', 'avatar:civ_borean_miner_m', 'avatar:civ_borean_artisan_f', 'avatar:civ_borean_youth_f', 'avatar:civ_borean_ranger_m'],
};
/** Wave 4: each civilisation's ELDERS (worn by the ids `founderIsElder` calls old). */
export const CIV_ELDERS: Readonly<Record<string, readonly string[]>> = {
  a: ['avatar:civ_aurelian_elder_m', 'avatar:civ_aurelian_elder_f'],
  b: ['avatar:civ_borean_elder_m', 'avatar:civ_borean_elder_f'],
};

export const BASE_AVATAR_KEYS: readonly string[] = [...new Set([...Object.values(BASE_ARCHETYPE_AVATARS).flat(), ...Object.values(BASE_CIV_CIVILIAN)])];
export const EXTRA_AVATAR_KEYS: readonly string[] = [...new Set([
  ...Object.values(EXTRA_ARCHETYPE_AVATARS).flat(), ...Object.values(CIV_ADULTS).flat(), ...Object.values(CIV_ELDERS).flat(),
])];
export const ALL_AVATAR_KEYS: readonly string[] = [...BASE_AVATAR_KEYS, ...EXTRA_AVATAR_KEYS];

let varietyRead: boolean | null = null;
/** The off switch, read once per page (a pool that changed mid-session would re-dress the whole town). */
export function versonaVarietyOn(): boolean {
  if (varietyRead !== null) return varietyRead;
  let on = true;
  try { if (typeof localStorage !== 'undefined' && localStorage.getItem('atlas:versona-variety') === 'off') on = false; } catch { /* no storage: on */ }
  varietyRead = on;
  return on;
}

/** The pre-wave rule, unchanged: a known civilisation's civilian wears its one body; anyone else `id mod pool`. */
export function baseAvatarFor(archetype: string, versonaId: bigint, civ: string | null = null): string {
  if (archetype === 'civilian' && civ && BASE_CIV_CIVILIAN[civ]) return BASE_CIV_CIVILIAN[civ];
  const list = BASE_ARCHETYPE_AVATARS[archetype] ?? BASE_ARCHETYPE_AVATARS.civilian;
  const n = BigInt(list.length);
  return list[Number(((versonaId % n) + n) % n)];
}

/** A well-mixed index from an id (sequential ids must not land on bodies in a visible pattern — THE HASH LAW). */
function pick(versonaId: bigint, n: number, salt: number): number {
  let h = (Number(BigInt.asUintN(32, versonaId)) ^ salt) >>> 0;
  h ^= h >>> 16; h = Math.imul(h, 0x85ebca6b); h ^= h >>> 13; h = Math.imul(h, 0xc2b2ae35); h ^= h >>> 16;
  return (h >>> 0) % n;
}

/** The pools as the rule reads them, built once (the rule runs per versona per refresh). */
const ADULT_POOL: Readonly<Record<string, readonly string[]>> = Object.fromEntries(
  Object.keys(BASE_CIV_CIVILIAN).map((civ) => [civ, [BASE_CIV_CIVILIAN[civ], ...(CIV_ADULTS[civ] ?? [])]]));
const ARCHETYPE_POOL: Readonly<Record<string, readonly string[]>> = Object.fromEntries(
  Object.keys(BASE_ARCHETYPE_AVATARS).map((arch) => [arch, [...BASE_ARCHETYPE_AVATARS[arch], ...(EXTRA_ARCHETYPE_AVATARS[arch] ?? [])]]));

/** The body a versona wears. `variety` false = the pre-wave rule (the off switch; tests pass it explicitly). */
export function avatarFor(archetype: string, versonaId: bigint, civ: string | null = null, variety: boolean = versonaVarietyOn()): string {
  if (!variety) return baseAvatarFor(archetype, versonaId, civ);
  if (archetype === 'civilian' && civ && ADULT_POOL[civ]) {
    const elders = CIV_ELDERS[civ] ?? [];
    if (elders.length > 0 && founderIsElder(versonaId)) return elders[pick(versonaId, elders.length, 0x5e1de7)];
    const adults = ADULT_POOL[civ];
    return adults[pick(versonaId, adults.length, 0xad017)];
  }
  const list = ARCHETYPE_POOL[archetype] ?? ARCHETYPE_POOL.civilian;
  return list[pick(versonaId, list.length, 0xa2c4e7)];
}

/**
 * The body to SHOW where only registered bodies can be shown (a preview that loads one asset): the versona's own body
 * when the catalogue holds it, else the pre-wave body it stands in — a shard that never ingested the wave shows people,
 * not placeholders.
 */
export function shownAvatarFor(archetype: string, versonaId: bigint, civ: string | null, registered: (key: string) => boolean): string {
  const ideal = avatarFor(archetype, versonaId, civ);
  return registered(ideal) ? ideal : baseAvatarFor(archetype, versonaId, civ);
}
